import React, {Fragment, useEffect} from "react";
import {useRouter} from "next/router";
import shopApi from "../../api/shop";
import {IBlogsLocale} from "../../types/pages";
import {domainUrl} from "../../helper";
import {GetServerSideProps, NextPage} from "next";
import {TranslationsType} from "../../types/homePageTypes";
import Head from "next/head";
import BlogPagePost from "../../components/blog/BlogPagePost";
import MainLayout, {ILayout} from "../../components/ForCWV/Wrappers/MainLayout";
import {layoutTranslations} from "../../services/translationKeys";
import {layoutProps} from "../../services/layoutProps";

interface IProps extends ILayout {
    translationsData: TranslationsType;
    selectedLocale: string,
    dbName: string,
    domain: string,
    breadcrumb: Array<{ title: string; url: string }>;
    blog: Array<IBlogsLocale>;
    siteName: string
}

const Blog: NextPage<IProps> = (pageProps) => {
    const {
        dbName,
        domain,
        selectedLocale,
        breadcrumb,
        blog,
        siteName,
        defaultLangCode,
        locales
    } = pageProps
    const router = useRouter();
    const {query} = router;
    const meta_title = blog[0]?.meta_title || blog[0]?.blog_title
    const meta_description = blog[0]?.meta_description || blog[0]?.blog_title
    const meta_keywords = blog[0]?.meta_keywords || blog[0]?.blog_title
    const meta_image = blog[0]?.image
    const meta_url = domainUrl(`${dbName}${router.locale !== "catchAll" && router.locale !== defaultLangCode ? "/" + selectedLocale : ""}${router.asPath}`)

    useEffect(() => {
        window.onpopstate = () => {
            router.push(window.location.pathname)
        }
    }, [query.pageSlug, selectedLocale, router.route])


    return (
        <MainLayout {...pageProps}>
            <Fragment>
                <Head>
                    <title>{meta_title}</title>
                    <meta name="title" content={meta_title}/>
                    <meta name="description" content={meta_description}/>
                    <meta name="keywords" content={meta_keywords}/>
                    <meta name="generator" content="Zegashop.com"/>
                    <meta name="robots" content="index, follow"/>
                    <meta name="twitter:image" content={meta_image}/>
                    <meta name="twitter:description" content={meta_description}/>
                    <meta name="twitter:title" content={meta_title}/>
                    <meta property="og:description" content={meta_description}/>
                    <meta property="og:title" content={meta_title}/>
                    <meta property="og:keywords" content={meta_keywords}/>
                    <meta property="og:image" content={meta_image}/>
                    <meta property="og:url" content={meta_url}/>
                    <meta property="og:locale" content={selectedLocale}/>
                    <meta property="og:site_name" content={siteName}/>
                    <link rel="canonical" href={meta_url}/>
                    {locales?.length > 0 ? (
                        <>
                            <link rel="alternate" href={domainUrl(`${dbName}${router.asPath}`)} hrefLang="x-default"/>
                            <link rel="alternate" href={domainUrl(`${dbName}${router.asPath}`)} hrefLang={defaultLangCode}/>
                            {locales.map(intl => (
                                <link rel="alternate" href={domainUrl(`${dbName}/${intl.code}${router.asPath}`)} hrefLang={intl.code}/>
                            ))}
                        </>
                    ) : null}
                </Head>
                <BlogPagePost
                    dbName={dbName}
                    breadcrumb={breadcrumb}
                    blogSlug={query.blogSlug}
                    locale={selectedLocale}
                    blog={blog}
                    domain={domain}
                    forBLogType={"blog"}
                />
            </Fragment>
        </MainLayout>
    );
}

export const getServerSideProps: GetServerSideProps = async ({locale, req, query}) => {
    const {blogSlug} = query;
    let blog = null;
    const typedReq = req as unknown as {
        m: { [key: string]: any }[],
        defaultDB: { db_name: string },
        assets: { logo_image: string },
        ipv4: string
    }
    const wrapperProps = await layoutProps({
        requestHeaders: req.headers,
        requestDbName: typedReq.defaultDB.db_name,
        requestModels: typedReq.m,
        requestLogo: typedReq.assets.logo_image,
        nextJSLocale: locale,
        translationsObject: layoutTranslations,
        ipv4: typedReq.ipv4
    })
    const { maintenanceInfo, suspendStatus, selectedLocale, translationsData: {Home: HomeIntl, Blog: BlogIntl} } = wrapperProps;
    if (String(suspendStatus) === "469") {
        return {
            redirect: { destination: '/suspend', permanent: false },
        }
    }
    if (maintenanceInfo?.is_maintenance_on) {
        return {
            redirect: { destination: '/maintenance', permanent: false },
        }
    }

    if (blogSlug !== "undefined") {
        const blogSlugOptions = {
            url_key: blogSlug,
            locale: selectedLocale,
            dbName: typedReq.defaultDB.db_name
        }
        const blogs = await shopApi.getAsyncBlogsBySlug(blogSlugOptions, typedReq.m)

        if (!blogs.data || blogs.data.length === 0) {
            return {
                notFound: true
            }
        }
        blog = blogs.data
    }

    const breadcrumb = [
        {title: HomeIntl, url: `/`},
        {title: BlogIntl, url: `/blog`},
        {title: blog[0]?.blog_title, url: ``}
    ]
    return {
        props: { blog, breadcrumb, locale: selectedLocale, ...wrapperProps},
    };
}

export default Blog;